import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, SlideInLeft, SlideInRight } from '../components/AnimatedSection';
import { ExternalLink, Globe } from 'lucide-react';

const Context = () => {
  const { t } = useLanguage();

  const sections = [
    {
      key: 'section1',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
      link: 'https://www.iconbuild.com',
      linkText: 'iconbuild.com'
    },
    {
      key: 'section2',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      link: 'https://cobod.com/projects/',
      linkText: 'cobod.com'
    },
    {
      key: 'section3',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
      link: 'https://www.weforum.org/agenda/2023/03/3d-printed-homes-construction/',
      linkText: 'weforum.org'
    },
    {
      key: 'section4',
      image: 'https://images.unsplash.com/photo-1580894894513-541e068a3e2b?w=800&q=80',
      link: 'https://www.defense.gov/News/Feature-Stories/Story/Article/3142267/additive-manufacturing-supports-military-readiness/',
      linkText: 'defense.gov'
    },
    {
      key: 'section5',
      image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80',
      link: 'https://www.mayoclinic.org/medical-professionals/orthopedic-surgery/news/3d-printing-in-medicine/mac-20430503',
      linkText: 'mayoclinic.org'
    },
    {
      key: 'section6',
      image: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800&q=80',
      link: 'https://www.bmwgroup.com/en/innovation/additive-manufacturing.html',
      linkText: 'bmwgroup.com'
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="context-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('context.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="context-title">
              {t('context.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl mb-8">
              {t('context.subtitle')}
            </p>
          </FadeInUp>
          
          <FadeInUp delay={0.3}>
            <p className="text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
              {t('context.intro')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Content Sections */}
      {sections.map((section, index) => (
        <section 
          key={section.key}
          className={`py-20 ${index % 2 === 1 ? 'bg-slate-50' : 'bg-white'}`}
          data-testid={`context-${section.key}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {index % 2 === 0 ? (
                <>
                  <SlideInLeft>
                    <div className="relative">
                      <img
                        src={section.image}
                        alt={t(`context.${section.key}.title`)}
                        className="w-full aspect-[4/3] object-cover"
                      />
                      <div className="absolute bottom-4 left-4">
                        <a
                          href={section.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 text-xs font-semibold text-navy hover:text-[#0066CC] transition-colors"
                        >
                          <Globe className="w-3.5 h-3.5" />
                          {section.linkText}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </SlideInLeft>
                  
                  <SlideInRight>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6">
                        {t(`context.${section.key}.title`)}
                      </h2>
                      <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                        {t(`context.${section.key}.text`)}
                      </p>
                    </div>
                  </SlideInRight>
                </>
              ) : (
                <>
                  <SlideInLeft className="lg:order-2">
                    <div className="relative">
                      <img
                        src={section.image}
                        alt={t(`context.${section.key}.title`)}
                        className="w-full aspect-[4/3] object-cover"
                      />
                      <div className="absolute bottom-4 left-4">
                        <a
                          href={section.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 text-xs font-semibold text-navy hover:text-[#0066CC] transition-colors"
                        >
                          <Globe className="w-3.5 h-3.5" />
                          {section.linkText}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </SlideInLeft>
                  
                  <SlideInRight className="lg:order-1">
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-navy mb-6">
                        {t(`context.${section.key}.title`)}
                      </h2>
                      <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                        {t(`context.${section.key}.text`)}
                      </p>
                    </div>
                  </SlideInRight>
                </>
              )}
            </div>
          </div>
        </section>
      ))}
    </Layout>
  );
};

export default Context;
